import React, { useState, useEffect, useCallback, useContext, useMemo, useId } from 'react';
import PropTypes from 'prop-types';
import { Spinner } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRefresh } from '@fortawesome/free-solid-svg-icons';
import BZAPIService from '../../services/BZAPIService';
import { VendorsContext } from '../../context/VendorsContext';
import { findProduct } from '../bz/products';
import { IcoSearch, IcoX } from '../bz/icons';
import { VendorMark } from '../bsa/projects/ProjectAtoms';
import '../bsa/projects/projectsList.css';
import {
  GROUPS,
  OTHER_VENDOR,
  defaultVendor,
  filterProjects,
  groupOf,
  productRows,
  filterProducts,
  projectsOf,
  sourceFilters,
  summaryCards,
  vendorRows,
  vendorSummary
} from './predictCoverageModel';
import PredictSbomCount from './PredictSbomCount';

// The SBOM panel shows this many projects until "Show all" is clicked.
const PROJECTS_SHOWN = 60;
// The managed products table shows this many rows before it scrolls inside its card.
const PRODUCTS_VISIBLE = 10;
const PRODUCT_ROW_PX = 41;

const small = { fontSize: 'var(--fs-sm)' };
// A project's dot: `scored` means Predict scored one of its bugs in the last 7 days, `notYet` that
// none came in, `unknown` that Predict's results can't be read (STRY0039045).
const statusLabel = status =>
  status === 'scored' ? 'Scored this week' : status === 'unknown' ? 'Status unavailable' : 'No new bugs this week';
const statusDot = status => (status === 'scored' ? 'var(--ok)' : status === 'unknown' ? 'var(--line)' : 'var(--info)');
const STATUSES = ['scored', 'notYet', 'unknown'];

// The dot, named for screen readers on a project; in the legend the words beside it name it.
const StatusDot = ({ status, named }) => (
  <span
    {...(named ? { role: 'img', 'aria-label': statusLabel(status) } : { 'aria-hidden': true })}
    style={{
      display: 'inline-block',
      flex: 'none',
      width: 7,
      height: 7,
      borderRadius: 999,
      verticalAlign: 'middle',
      background: statusDot(status)
    }}
  />
);
StatusDot.propTypes = { status: PropTypes.string, named: PropTypes.bool };

const Pill = ({ tone, children }) => (
  <span className={`bz-pill${tone && tone !== 'neutral' ? ` bz-pill--${tone}` : ''}`}>{children}</span>
);
Pill.propTypes = { tone: PropTypes.string, children: PropTypes.node.isRequired };

const SummaryCard = ({ label, value, meta }) => (
  <div className="bz-stat" role="group" aria-label={label}>
    <div className="bz-stat__label" aria-hidden="true">
      {label}
    </div>
    <div className="bz-stat__value">{value}</div>
    <div className="bz-stat__meta">{meta}</div>
  </div>
);
SummaryCard.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  meta: PropTypes.string.isRequired
};

const VendorList = ({ rows, selected, onSelect }) => (
  <nav aria-label="Your vendors" className="bz-card" style={{ flex: '1 1 260px', maxWidth: 340, overflow: 'hidden' }}>
    <div className="bz-kicker" style={{ padding: '12px 14px', borderBottom: '1px solid var(--line-soft)' }}>
      Your vendors
    </div>
    {GROUPS.map(group => {
      const members = rows.filter(row => row.group === group.id);
      if (!members.length) return null;
      return (
        <div key={group.id} role="group" aria-label={group.label}>
          <div className="bz-kicker bz-muted" style={{ padding: '10px 14px 4px' }}>
            {group.label}
          </div>
          {members.map(row => {
            const on = row.id === selected;
            return (
              <button
                key={row.id}
                type="button"
                aria-pressed={on}
                onClick={() => onSelect(row.id)}
                className="bz-row"
                style={{
                  width: '100%',
                  gap: 10,
                  padding: '8px 14px',
                  border: 0,
                  borderLeft: `3px solid ${on ? 'var(--prevent)' : 'transparent'}`,
                  background: on ? 'var(--bg-tan)' : 'transparent',
                  textAlign: 'left',
                  font: 'inherit',
                  color: 'var(--ink)',
                  cursor: 'pointer'
                }}
              >
                <VendorMark vendorId={row.id} label={row.name} size={22} />
                <span className="bz-grow bz-truncate">{row.name}</span>
                <span className="bz-muted bz-tnum" style={small}>
                  {row.oss ? `${row.oss} OSS` : '—'}
                </span>
              </button>
            );
          })}
        </div>
      );
    })}
  </nav>
);
VendorList.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string, name: PropTypes.string, group: PropTypes.string }))
    .isRequired,
  selected: PropTypes.string,
  onSelect: PropTypes.func.isRequired
};

const ProductsCard = ({ row }) => {
  const [state, setState] = useState({ loading: true, failed: false, products: [] });
  const vendorId = row.id;

  useEffect(() => {
    let live = true;
    if (vendorId === OTHER_VENDOR) {
      setState({ loading: false, failed: false, products: [] });
      return undefined;
    }
    setState({ loading: true, failed: false, products: [] });
    BZAPIService.listManagedProducts(vendorId)
      .then(products => live && setState({ loading: false, failed: false, products: products || [] }))
      .catch(() => live && setState({ loading: false, failed: true, products: [] }));
    return () => {
      live = false;
    };
  }, [vendorId]);

  const [query, setQuery] = useState('');
  const [sort, setSort] = useState('name');
  const rows = useMemo(() => productRows(state.products, row), [state.products, row]);
  const hasSbom = rows.some(p => p.projects > 0 || p.sources.length > 0);
  const shown = useMemo(() => filterProducts(rows, { query, sort: hasSbom ? sort : 'name' }), [
    rows,
    query,
    sort,
    hasSbom
  ]);
  const sorts = hasSbom
    ? [
        ['name', 'A–Z'],
        ['hosts', 'Most hosts'],
        ['projects', 'Most OSS']
      ]
    : [];

  return (
    <div className="bz-card">
      <div className="bz-row" style={{ padding: '12px 16px', borderBottom: '1px solid var(--line-soft)' }}>
        <h3 className="bz-h3 bz-grow" style={{ margin: 0 }}>
          Managed products
        </h3>
        <span className="bz-muted" style={small}>
          from your Prevent inventory
        </span>
      </div>
      {state.loading ? (
        <div className="text-center" style={{ padding: 24 }}>
          <Spinner size="sm" color="primary" />
        </div>
      ) : state.failed ? (
        <div className="bz-muted" style={{ padding: 16 }}>
          Couldn&apos;t load this vendor&apos;s products.
        </div>
      ) : rows.length === 0 ? (
        <div className="bz-muted" style={{ padding: 16 }}>
          No managed products yet.
        </div>
      ) : (
        <>
          <div className="bz-row" style={{ gap: 8, flexWrap: 'wrap', padding: '12px 16px 0' }}>
            <label className="bz-input pl-search" style={{ flex: '1 1 240px' }}>
              <IcoSearch size={14} />
              <input
                type="search"
                value={query}
                placeholder="Search products"
                aria-label="Search products"
                onChange={e => setQuery(e.target.value)}
              />
              {query && (
                <button
                  type="button"
                  className="pl-search__clear"
                  aria-label="Clear search"
                  onClick={() => setQuery('')}
                >
                  <IcoX size={12} />
                </button>
              )}
            </label>
            <span className="bz-muted" style={small}>
              {shown.length === rows.length ? `${rows.length} products` : `${shown.length} of ${rows.length} products`}
            </span>
            {sorts.length > 0 && (
              <div role="group" aria-label="Sort products" className="bz-row" style={{ gap: 4, marginLeft: 'auto' }}>
                <span className="bz-muted" style={small}>
                  Sort
                </span>
                {sorts.map(([id, label]) => (
                  <button
                    key={id}
                    type="button"
                    aria-pressed={sort === id}
                    className={`bz-btn bz-btn--sm${sort === id ? '' : ' bz-btn--ghost'}`}
                    onClick={() => setSort(id)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div
            data-testid="products-scroll"
            style={{
              overflow: 'auto',
              maxHeight: rows.length > PRODUCTS_VISIBLE ? (PRODUCTS_VISIBLE + 1) * PRODUCT_ROW_PX : undefined,
              marginTop: 12
            }}
          >
            <table className="bz-table" style={{ width: '100%' }}>
              <thead style={{ position: 'sticky', top: 0, zIndex: 1, background: 'var(--bg)' }}>
                <tr>
                  <th>Product</th>
                  {hasSbom && <th>Versions found</th>}
                  {hasSbom ? <th>With an SBOM</th> : <th>Covered by</th>}
                  {hasSbom && <th style={{ textAlign: 'right' }}>OSS</th>}
                </tr>
              </thead>
              <tbody>
                {shown.length === 0 && (
                  <tr>
                    <td className="bz-muted" colSpan={hasSbom ? 4 : 2}>
                      No product matches.
                    </td>
                  </tr>
                )}
                {shown.map(p => (
                  <tr key={p.id}>
                    <td style={{ fontWeight: 500 }}>{p.name}</td>
                    {hasSbom && <td className="bz-muted">{p.versions || '—'}</td>}
                    {hasSbom ? (
                      <td>
                        <PredictSbomCount product={p} vendorName={row.name} />
                      </td>
                    ) : (
                      <td className="bz-muted">{p.covered}</td>
                    )}
                    {hasSbom && (
                      <td className="bz-tnum" style={{ textAlign: 'right' }}>
                        {p.projects || '—'}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};
ProductsCard.propTypes = { row: PropTypes.object.isRequired };

const SbomCard = ({ row }) => {
  const [query, setQuery] = useState('');
  const [source, setSource] = useState(null);
  const [sort, setSort] = useState('hosts');
  const [showAll, setShowAll] = useState(false);
  const headingId = useId();

  const projects = useMemo(() => projectsOf(row), [row]);
  const filters = useMemo(() => sourceFilters(projects), [projects]);
  const shown = useMemo(() => filterProjects(projects, { query, source, sort }), [projects, query, source, sort]);
  const visible = showAll ? shown : shown.slice(0, PROJECTS_SHOWN);
  // The legend: each dot the list shows, with how many projects carry it.
  const byStatus = STATUSES.map(status => ({
    status,
    count: projects.filter(p => (STATUSES.includes(p.status) ? p.status : 'notYet') === status).length
  })).filter(s => s.count > 0);

  return (
    // A div, not a <section>: the legacy theme pads every section by 4rem.
    <div role="region" className="bz-card" aria-labelledby={headingId}>
      <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--line-soft)' }}>
        <h3 id={headingId} className="bz-h3" style={{ margin: 0 }}>
          OSS inside {row.name} (SBOM)
        </h3>
        <div className="bz-muted" style={{ fontSize: 'var(--fs-md)', marginTop: 4 }}>
          Built from the versions found in your inventory.
        </div>
        <div className="bz-muted" style={{ ...small, marginTop: 4 }}>
          {projects.length} OSS
          {byStatus.map(({ status, count }) => (
            <React.Fragment key={status}>
              {' · '}
              <StatusDot status={status} /> {count} {statusLabel(status).toLowerCase()}
            </React.Fragment>
          ))}
        </div>
      </div>
      <div className="bz-row" style={{ gap: 8, flexWrap: 'wrap', padding: '12px 16px 0' }}>
        <label className="bz-input pl-search" style={{ flex: '1 1 240px' }}>
          <IcoSearch size={14} />
          <input
            type="search"
            value={query}
            placeholder="Search OSS"
            aria-label="Search OSS"
            onChange={e => setQuery(e.target.value)}
          />
          {query && (
            <button type="button" className="pl-search__clear" aria-label="Clear search" onClick={() => setQuery('')}>
              <IcoX size={12} />
            </button>
          )}
        </label>
        <div role="group" aria-label="SBOM from" className="bz-row" style={{ gap: 6, flexWrap: 'wrap' }}>
          {filters.map(f => (
            <button
              key={f.label}
              type="button"
              aria-pressed={source === f.id}
              className={`bz-btn bz-btn--sm${source === f.id ? ' bz-btn--primary' : ''}`}
              onClick={() => setSource(f.id)}
            >
              {f.label} <span style={{ opacity: 0.7, marginLeft: 4 }}>{f.count}</span>
            </button>
          ))}
        </div>
        <div role="group" aria-label="Sort OSS" className="bz-row" style={{ gap: 4, marginLeft: 'auto' }}>
          <span className="bz-muted" style={small}>
            Sort
          </span>
          {[
            ['hosts', 'Most hosts'],
            ['name', 'A–Z']
          ].map(([id, label]) => (
            <button
              key={id}
              type="button"
              aria-pressed={sort === id}
              className={`bz-btn bz-btn--sm${sort === id ? '' : ' bz-btn--ghost'}`}
              onClick={() => setSort(id)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      <ul
        aria-label="OSS"
        style={{ listStyle: 'none', margin: 0, padding: '12px 16px', display: 'flex', flexWrap: 'wrap', gap: 8 }}
      >
        {visible.length === 0 && <li className="bz-muted">No OSS matches.</li>}
        {visible.map(p => (
          <li
            key={p.target}
            title={[statusLabel(p.status), p.detail, p.sources.join(', ')].filter(Boolean).join(' · ')}
            className="bz-row"
            style={{
              gap: 6,
              padding: '4px 10px',
              borderRadius: 'var(--r-pill)',
              border: '1px solid var(--line)',
              background: 'var(--bg-soft)',
              fontSize: 'var(--fs-base)'
            }}
          >
            <StatusDot status={p.status} named />
            <span>{p.name}</span>
            <span className="bz-muted">· {p.hosts} hosts</span>
          </li>
        ))}
      </ul>
      {shown.length > PROJECTS_SHOWN && (
        <div style={{ padding: '0 16px 12px' }}>
          <button type="button" className="bz-btn bz-btn--ghost bz-btn--sm" onClick={() => setShowAll(v => !v)}>
            {showAll ? 'Show fewer' : `Show all ${shown.length} OSS`}
          </button>
        </div>
      )}
    </div>
  );
};
SbomCard.propTypes = { row: PropTypes.object.isRequired };

const VendorDetail = ({ row }) => {
  const group = groupOf(row.group);
  const hasProjects = row.entries.some(entry => entry.kind === 'project');
  return (
    <div
      role="region"
      aria-label={row.name}
      style={{ flex: '999 1 560px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 16 }}
    >
      <div className="bz-card bz-row" style={{ padding: '16px 20px', gap: 14, flexWrap: 'wrap' }}>
        <VendorMark vendorId={row.id} label={row.name} size={36} />
        <div className="bz-grow" style={{ minWidth: 240 }}>
          <h2 className="bz-h2" style={{ margin: 0 }}>
            {row.name}
          </h2>
          <div className="bz-muted" style={{ fontSize: 'var(--fs-md)', marginTop: 2 }}>
            {vendorSummary(row)}
          </div>
        </div>
        <Pill tone={group.tone}>{group.label}</Pill>
      </div>
      {row.id !== OTHER_VENDOR && <ProductsCard row={row} />}
      {hasProjects && <SbomCard key={row.id} row={row} />}
    </div>
  );
};
VendorDetail.propTypes = { row: PropTypes.object.isRequired };

/**
 * Predict → Coverage (STRY0038969, redesigned in STRY0039118) — what Predict
 * watches for this organization, in the customer's terms.
 *
 * Four summary cards, then the vendors the customer switched on in Prevent,
 * grouped by how Predict covers each (through a tracker or open-source
 * projects, per product or whole vendor, waiting for a run, nothing to score,
 * not set up), beside the selected vendor's
 * detail: its managed products (versions found and hosts for products with an
 * SBOM) and the open-source projects inside them, searchable and sorted.
 *
 * Read-only, real data only: GET /predict/scope (filtered by the caller's
 * vendor READ), the shell's vendor list (VendorsContext) and the selected
 * vendor's managed products (GET /managedProducts, vendor READ again).
 */
const PredictCoverage = () => {
  const [vendors] = useContext(VendorsContext);
  const [isLoading, setIsLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [entries, setEntries] = useState([]);
  const [coverage, setCoverage] = useState({});
  // The rest of GET /predict/scope: {reasons, coverageAvailable}, from Predict's coverage map in ODD.
  const [about, setAbout] = useState({});
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    setLoadFailed(false);
    try {
      const response = await BZAPIService.getPredictScope();
      setEntries(Array.isArray(response?.entries) ? response.entries : []);
      setCoverage(response?.coverage && typeof response.coverage === 'object' ? response.coverage : {});
      setAbout({
        reasons: response?.reasons && typeof response.reasons === 'object' ? response.reasons : {},
        coverageAvailable: response?.coverageAvailable !== false
      });
    } catch (error) {
      console.error('Failed to load the Predict coverage:', error);
      setLoadFailed(true);
      setEntries([]);
      setCoverage({});
      setAbout({});
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Until the vendor list loads, or for a vendor it doesn't know, the id stands in.
  const vendorNameOf = useMemo(() => {
    const names = new Map((Array.isArray(vendors) ? vendors : []).filter(Boolean).map(v => [v.id, v.name]));
    return id => names.get(id) || id;
  }, [vendors]);

  const rows = useMemo(() => vendorRows(vendors, entries, coverage, vendorNameOf, about), [
    vendors,
    entries,
    coverage,
    vendorNameOf,
    about
  ]);
  const cards = useMemo(() => summaryCards(rows, entries, vendorNameOf), [rows, entries, vendorNameOf]);
  const current = rows.find(row => row.id === selected) || rows.find(row => row.id === defaultVendor(rows));

  return (
    <div
      style={{
        padding: '24px 32px',
        maxWidth: 1320,
        margin: '0 auto',
        display: 'flex',
        flexDirection: 'column',
        gap: 24
      }}
    >
      <div className="bz-section-head">
        <div>
          <div className="bz-kicker" style={{ color: findProduct('predict').accent }}>
            Predict · Coverage
          </div>
          <h1 className="bz-h1" style={{ marginTop: 4 }}>
            What Predict watches for you
          </h1>
          <div className="bz-muted" style={{ fontSize: 'var(--fs-md)', marginTop: 4, maxWidth: 760 }}>
            Predict reads the bug trackers behind the products you run and scores every new bug for its chance of
            becoming a CVE. It follows the vendors you have switched on in Prevent.
          </div>
        </div>
        <button type="button" className="bz-btn bz-btn--sm" onClick={load} disabled={isLoading}>
          <FontAwesomeIcon icon={faRefresh} className={isLoading ? 'fa-spin' : ''} />
          {' Refresh'}
        </button>
      </div>

      {isLoading ? (
        <div className="text-center" style={{ padding: 40 }}>
          <Spinner color="primary" />
          <div className="bz-muted" style={{ marginTop: 8 }}>
            Loading coverage…
          </div>
        </div>
      ) : loadFailed ? (
        <div className="bz-card text-center" style={{ padding: 40 }}>
          <div style={{ fontWeight: 600 }}>Failed to load coverage</div>
        </div>
      ) : (
        <>
          <div
            role="region"
            aria-label="Summary"
            style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}
          >
            {cards.map(card => (
              <SummaryCard key={card.label} {...card} />
            ))}
          </div>
          {rows.length === 0 ? (
            <div className="bz-card text-center" style={{ padding: 40 }}>
              <div style={{ fontWeight: 600 }}>No vendors switched on yet</div>
              <div className="bz-muted" style={{ marginTop: 4 }}>
                Predict follows the vendors you switch on in Prevent.
              </div>
            </div>
          ) : (
            <div className="bz-row" style={{ gap: 20, alignItems: 'flex-start', flexWrap: 'wrap' }}>
              <VendorList rows={rows} selected={current?.id} onSelect={setSelected} />
              {current && <VendorDetail row={current} />}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PredictCoverage;
